import GetAppIcon from '@mui/icons-material/GetApp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Button,
  ButtonProps,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  GridCsvGetRowsToExportParams,
  GridExcelExportOptions,
  gridSortedRowIdsSelector,
  useGridApiContext
} from '@mui/x-data-grid-premium';
import ImportFileUploader from 'components/ui/file-uploader/ImportFileUploader';
import { IsSuperAdmin } from 'shared/permissions/IsSuperAdmin';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import toast from 'shared/utils/toast';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToolbarAction } from '../../../types/toolbar.types';

const buttonProps: ButtonProps = {
  size: 'medium',
  className: 'normal-case font-normal'
};

interface ActionSectionProps {
  /**
   * Core data service instance containing integration API endpoints.
   * Manages specialized backend export routines (`exportAll`, `exportSummary`)
   * and administrative file parsing tasks.
   * @type {any}
   */
  service: any;
  /**
   * Additional query parameters, filter states, or context variables sent from the parent container.
   * Automatically passed downstream to backend export routines to ensure correct data scope extraction.
   * @type {Record<string, unknown>}
   */
  outerParams?: Record<string, unknown>;
  entityId?: number;
  /**
   * Flag to restrict data extraction utilities across the interface.
   * When set to `true`, primary and advanced spreadsheet export features are completely disabled.
   * @type {boolean}
   * @default false
   */
  disableExport?: boolean;
  /**
   * Dynamic array containing functional action configurations rendered sequentially on the row.
   * Processes custom user buttons, compact icon tools, and complex nested drop-down layouts.
   * @type {ToolbarAction[]}
   */
  actions: ToolbarAction[];
}

export const ActionSection = ({
  service,
  outerParams,
  entityId,
  disableExport,
  actions
}: ActionSectionProps) => {
  const isAdmin = useIsTenantAdministrator();
  const { t } = useTranslation();
  const apiRef = useGridApiContext();

  // Gestione stato locale per menu a tendina dinamici multipli
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeActionId, setActiveActionId] = useState<string | null>(null);

  const handleOpenDropdown = (event: React.MouseEvent<HTMLElement>, actionId: string) => {
    setAnchorEl(event.currentTarget);
    setActiveActionId(actionId);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setActiveActionId(null);
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, action: ToolbarAction) => {
    //  Se è definito un onClick custom nel componente padre, esegui quello
    if (action.onClick) {
      action.onClick(event);
      return;
    }

    //  Altrimenti, esegui dinamicamente la funzione della griglia passata come stringa chiave
    if (apiRef?.current && action.gridAction) {
      const targetMethod = apiRef.current[action.gridAction];

      if (typeof targetMethod === 'function') {
        const executableMethod = targetMethod as () => unknown;
        executableMethod();
      } else {
        console.warn(
          `Il metodo ${action.gridAction} esiste su apiRef ma non è una funzione invocabile.`
        );
      }
    }
  };

  const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) =>
    gridSortedRowIdsSelector(apiRef);

  const handleExport = (options: GridExcelExportOptions) => {
    if (apiRef?.current) {
      apiRef.current.exportDataAsExcel(options);
    }
  };

  const exportAll = () => {
    if (!service) return;
    if (outerParams) service.exportAll(outerParams);
    else service.exportAll();
    toast.info(t('messages.export-required'));
  };

  const exportSummary = () => {
    if (!service) return;
    if (outerParams) service.exportSummary(outerParams);
    else service.exportSummary();
    toast.info(t('messages.export-required'));
  };

  // Build the baseline default array actions
  const defaultActions: ToolbarAction[] = [];

  if (isAdmin) {
    // !CHIEDERE STEFANO
    if (!disableExport) {
      defaultActions.push({
        id: 'download-filtered-rows',
        label: t('data-grid.download-filtered-rows'),
        onClick: () => handleExport({ getRowsToExport: getFilteredRows }),
        startIcon: <GetAppIcon />,
        actionType: 'button',
        variant: 'text',
        color: 'primary'
      });
    }
    if (service.exportAll && !disableExport) {
      defaultActions.push({
        id: 'export-all',
        label: t('data-grid.export-all'),
        onClick: exportAll,
        actionType: 'button',
        variant: 'contained',
        color: 'success'
      });
    }
    if (service.exportSummary) {
      defaultActions.push({
        id: 'export-summary',
        label: t('data-grid.export-summary'),
        onClick: exportSummary,
        actionType: 'button',
        variant: 'contained',
        color: 'success'
      });
    }
  }

  const mergedActions = [...defaultActions, ...actions];
  const visibleActions = mergedActions.filter((action) => action.visible !== false);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        flexWrap: 'nowrap',
        flexShrink: 0,
        marginLeft: 'auto'
      }}>
      <IsSuperAdmin>
        {(!!service.importFromFile || !!service.importFromFileDetail) && (
          <ImportFileUploader service={service} entityId={entityId} />
        )}
      </IsSuperAdmin>

      {visibleActions.map((action, index) => {
        if (action.component) {
          return (
            <React.Fragment key={action.id || `custom-action-${index}`}>
              {action.component}
            </React.Fragment>
          );
        }

        const type = action.actionType || 'button';

        // CASE 1: Icon Only Button with Tooltip
        if (type === 'iconButton') {
          return (
            <Tooltip key={action.id} title={action.label} arrow placement="top">
              <span>
                <IconButton
                  {...buttonProps}
                  disabled={action.disabled}
                  onClick={(e) => handleActionClick(e, action)}
                  sx={{
                    width: 40,
                    height: 40,
                    border: action.variant === 'outlined' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}>
                  {action.startIcon}
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        // CASE 2: Dropdown Menu Button
        if (type === 'dropdown') {
          const isCurrentDropdownOpen = anchorEl !== null && activeActionId === action.id;

          return (
            <React.Fragment key={action.id}>
              <Button
                {...buttonProps}
                startIcon={action.startIcon}
                endIcon={<KeyboardArrowDownIcon />}
                disabled={action.disabled}
                onClick={(e) => {
                  handleOpenDropdown(e, action.id);
                  handleActionClick(e, action);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  height: '40px',
                  px: 2.5,
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                  minWidth: 'max-content',
                  '&:hover': { boxShadow: 'none' }
                }}>
                {action.label}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={isCurrentDropdownOpen}
                onClose={handleCloseDropdown}
                onClick={handleCloseDropdown}
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    mt: 0.5,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                    minWidth: 160
                  }
                }}>
                {action.dropdownItems?.map((item) => (
                  <MenuItem
                    key={item.id}
                    onClick={(e) => {
                      handleCloseDropdown();
                      handleActionClick(e, item);
                    }}
                    disabled={item.disabled}
                    sx={{ typography: 'body2', py: 1, px: 2 }}>
                    {item.icon && (
                      <ListItemIcon sx={{ minWidth: '32px !important' }}>{item.icon}</ListItemIcon>
                    )}
                    <ListItemText primary={item.label} />
                  </MenuItem>
                ))}
              </Menu>
            </React.Fragment>
          );
        }

        // CASE 3:  Default

        return (
          <Button
            {...buttonProps}
            variant={action.variant || 'text'}
            key={action.id}
            startIcon={action.startIcon}
            disabled={action.disabled}
            onClick={(e) => handleActionClick(e, action)}
            sx={{
              textTransform: 'none',
              backgroundColor: action.variant === 'text' ? 'none' : '#1d70b8',
              fontWeight: 600,
              fontSize: '0.875rem',
              borderRadius: action.variant === 'text' ? 0 : 2,
              px: action.variant === 'text' ? 1 : 2.5,
              height: '40px',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              minWidth: 'max-content',
              '& .MuiButton-startIcon': {
                color: action.variant === 'text' ? '#006DB0' : '#ffffff',
                marginRight: '6px'
              },
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: action.variant === 'text' ? 'rgba(0, 109, 176, 0.04)' : '#155a96',
                textDecoration: action.variant === 'text' ? 'underline' : 'none'
              }
            }}>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                lineHeight: '1.2', // 120%
                letterSpacing: '0em',
                textTransform: 'none',
                color: action.variant === 'text' ? '#006DB0' : '#ffffff'
              }}>
              {action.label}
            </Typography>
          </Button>
        );
      })}
    </Stack>
  );
};
